import Phaser from 'phaser';
import { COLORS, GAME_W, GAME_H, intToHex, LeaderboardEntry } from '../utils/constants';
import { subscribeToLeaderboard, clearBackendLeaderboard } from '../utils/leaderboard';

export default class LeaderboardScene extends Phaser.Scene {
  private promoted = false;
  private unsubscribe: (() => void) | null = null;
  private contentContainer: Phaser.GameObjects.Container | null = null;

  constructor() {
    super({ key: 'LeaderboardScene' });
  }

  init(data: { promoted?: boolean }) {
    this.promoted = data?.promoted ?? false;
  }

  create() {
    this.cameras.main.setBackgroundColor(COLORS.bgDark);

    const grid = this.add.graphics().setDepth(0);
    grid.lineStyle(1, COLORS.purple, 0.03);
    for (let x = 0; x < GAME_W; x += 40) grid.lineBetween(x, 0, x, GAME_H);
    for (let y = 0; y < GAME_H; y += 40) grid.lineBetween(0, y, GAME_W, y);

    this.add.text(GAME_W / 2, 40, '排行榜', {
      fontFamily: 'Microsoft YaHei, Arial Black, Arial, sans-serif',
      fontSize: '36px', color: intToHex(COLORS.purple),
      stroke: '#000000', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(10);

    this.contentContainer = this.add.container(0, 0).setDepth(10);

    this.unsubscribe = subscribeToLeaderboard((entries) => {
      this.renderLeaderboard(entries);
    });

    const backBtn = this.add.container(80, GAME_H - 50, [
      this.add.rectangle(0, 0, 160, 40, COLORS.purple).setOrigin(0.5).setAlpha(0.2),
      this.add.rectangle(0, 0, 160, 40, 0x000000).setOrigin(0.5).setStrokeStyle(2, COLORS.purple),
      this.add.text(0, 0, '返回', {
        fontFamily: 'Arial', fontSize: '16px', color: '#9945ff', fontStyle: 'bold',
      }).setOrigin(0.5),
    ]).setDepth(10);
    backBtn.setSize(160, 40).setInteractive({ useHandCursor: true });
    backBtn.on('pointerover', () => (backBtn.list[0] as Phaser.GameObjects.Rectangle).setAlpha(0.4));
    backBtn.on('pointerout', () => (backBtn.list[0] as Phaser.GameObjects.Rectangle).setAlpha(0.2));
    backBtn.on('pointerdown', () => {
      this.cameras.main.fadeOut(500, 0, 0, 0);
      this.time.delayedCall(500, () => this.scene.start('TitleScene'));
    });

    const clearBtn = this.add.container(GAME_W - 80, GAME_H - 50, [
      this.add.rectangle(0, 0, 160, 40, COLORS.red).setOrigin(0.5).setAlpha(0.2),
      this.add.rectangle(0, 0, 160, 40, 0x000000).setOrigin(0.5).setStrokeStyle(2, COLORS.red),
      this.add.text(0, 0, '清除排行榜', {
        fontFamily: 'Microsoft YaHei, Arial, sans-serif', fontSize: '14px', color: '#ff1744', fontStyle: 'bold',
      }).setOrigin(0.5),
    ]).setDepth(10);
    clearBtn.setSize(160, 40).setInteractive({ useHandCursor: true });
    clearBtn.on('pointerover', () => (clearBtn.list[0] as Phaser.GameObjects.Rectangle).setAlpha(0.4));
    clearBtn.on('pointerout', () => (clearBtn.list[0] as Phaser.GameObjects.Rectangle).setAlpha(0.2));
    clearBtn.on('pointerdown', () => {
      clearBackendLeaderboard();
    });

    this.input.keyboard!.on('keydown-ESC', () => this.goBack());
    this.input.keyboard!.on('keydown-ENTER', () => this.goBack());
  }

  shutdown() {
    this.unsubscribe?.();
  }

  private goBack() {
    this.cameras.main.fadeOut(500, 0, 0, 0);
    this.time.delayedCall(500, () => this.scene.start('TitleScene'));
  }

  private renderLeaderboard(entries: LeaderboardEntry[]) {
    if (this.contentContainer) {
      this.contentContainer.removeAll(true);
    }

    const statsY = 90;
    const bestScore = entries.length > 0 ? entries[0].score : 0;
    const longestCombo = entries.length > 0 ? Math.max(...entries.map(e => e.maxCombo || 0)) : 0;
    const highestRate = entries.length > 0 ? Math.max(...entries.map(e => e.hitRate || 0)) : 0;

    const items = [
      { label: '最高分', value: bestScore.toString(), color: COLORS.cyan },
      { label: '最长连击', value: longestCombo.toString(), color: COLORS.yellow },
      { label: '最高命中率', value: `${(highestRate * 100).toFixed(0)}%`, color: COLORS.green },
    ];

    items.forEach((item, i) => {
      const x = GAME_W / 2 + (i - 1) * 200;
      this.addText(x, statsY, item.label, {
        fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '12px', color: '#555577',
      });
      this.addText(x, statsY + 22, item.value, {
        fontFamily: 'Arial', fontSize: '22px', color: intToHex(item.color), fontStyle: 'bold',
      });
    });

    const tableY = 145, rowH = 38;
    const hx = [80, 230, 390, 530, 670, 830, 990];
    const headers = ['排名', '玩家', '分数', '评级', '时间', '命中率', '连击'];

    headers.forEach((h, i) => {
      this.addText(hx[i], tableY, h, {
        fontFamily: 'Arial', fontSize: '11px', color: '#555577', fontStyle: 'bold',
      });
    });

    const div = this.add.graphics().setDepth(10);
    div.lineStyle(1, COLORS.gray, 0.3);
    div.lineBetween(60, tableY + 18, GAME_W - 60, tableY + 18);
    this.contentContainer?.add(div);

    if (entries.length === 0) {
      this.addText(GAME_W / 2, tableY + 60, '暂无记录', {
        fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '18px', color: '#555577',
      });
      return;
    }

    entries.slice(0, 20).forEach((entry, i) => {
      const rowY = tableY + 28 + i * rowH;
      const isFirst = i === 0 && this.promoted;
      const rankColor = i === 0 ? '#ffea00' : i === 1 ? '#cccccc' : i === 2 ? '#ff9100' : '#555577';
      const rankText = i < 3 ? ['1st', '2nd', '3rd'][i] : `${i + 1}th`;
      const nameColor = isFirst ? '#ffea00' : '#00f0ff';

      const rowContainer = this.add.container(0, rowY).setDepth(10);
      this.contentContainer?.add(rowContainer);

      let rowBg: Phaser.GameObjects.Rectangle | null = null;
      if (i < 3) {
        rowBg = this.add.rectangle(GAME_W / 2, rowY, GAME_W - 120, rowH - 4,
          [COLORS.yellow, COLORS.gray, COLORS.pink][i], 0.08
        ).setDepth(9);
        this.contentContainer?.add(rowBg);
      }

      const mkText = (x: number, txt: string, style: any) => {
        const t = this.add.text(x, 0, txt, style).setOrigin(0.5);
        rowContainer.add(t);
        return t;
      };

      mkText(hx[0], rankText, { fontFamily: 'Arial', fontSize: '14px', color: rankColor, fontStyle: 'bold' });
      mkText(hx[1], (entry.playerName || '---').substring(0, 10), { fontFamily: 'Arial', fontSize: '13px', color: nameColor });
      mkText(hx[2], entry.score.toString(), { fontFamily: 'Arial', fontSize: '14px', color: isFirst ? '#ffea00' : '#ffffff' });

      const grade = entry.grade || this.calcGrade(entry.score);
      mkText(hx[3], grade, { fontFamily: 'Arial', fontSize: '14px', color: isFirst ? '#ffea00' : this.gradeColor(grade), fontStyle: 'bold' });

      const mins = Math.floor((entry.battleTime || 0) / 60);
      const secs = Math.floor((entry.battleTime || 0) % 60);
      mkText(hx[4], `${mins}:${secs.toString().padStart(2, '0')}`, { fontFamily: 'Arial', fontSize: '13px', color: '#aaaaaa' });
      mkText(hx[5], `${((entry.hitRate || 0) * 100).toFixed(0)}%`, { fontFamily: 'Arial', fontSize: '13px', color: '#aaaaaa' });
      mkText(hx[6], (entry.maxCombo || 0).toString(), { fontFamily: 'Arial', fontSize: '13px', color: '#aaaaaa' });

      // Slide-up animation from bottom when promoted
      if (this.promoted) {
        const delay = 200 + i * 100;
        rowContainer.setY(rowY + 300);
        rowContainer.setAlpha(0);
        this.tweens.add({
          targets: rowContainer,
          y: rowY,
          alpha: 1,
          duration: 500,
          delay,
          ease: 'Back.easeOut',
        });
        if (rowBg) {
          rowBg.setAlpha(0);
          this.tweens.add({ targets: rowBg, alpha: 0.08, duration: 400, delay });
        }
        if (isFirst) {
          this.time.delayedCall(delay + 100, () => {
            const flash = this.add.graphics().setDepth(12);
            flash.fillStyle(COLORS.yellow, 0.3);
            flash.fillRect(0, rowY - rowH / 2, GAME_W, rowH);
            this.tweens.add({ targets: flash, alpha: 0, duration: 600, onComplete: () => flash.destroy() });

            const crown = this.add.text(hx[0], rowY - 50, '👑', {
              fontFamily: 'Arial, sans-serif', fontSize: '26px',
            }).setOrigin(0.5).setDepth(11).setAlpha(0);
            this.tweens.add({ targets: crown, alpha: 1, y: rowY - 22, duration: 500, ease: 'Bounce.easeOut' });

            for (let r = 0; r < 3; r++) {
              const g = this.add.graphics().setDepth(9);
              g.fillStyle([COLORS.yellow, COLORS.cyan, COLORS.pink][r], 0.08);
              g.fillCircle(GAME_W / 2, rowY, rowH + r * 40);
              this.tweens.add({ targets: g, alpha: 0, duration: 1000 + r * 300, onComplete: () => g.destroy() });
            }
          });
        }
      }
    });
  }

  private addText(x: number, y: number, text: string, style: any) {
    const t = this.add.text(x, y, text, style).setOrigin(0.5).setDepth(10);
    this.contentContainer?.add(t);
    return t;
  }

  private calcGrade(score: number): string {
    if (score >= 95) return 'S+';
    if (score >= 85) return 'S';
    if (score >= 75) return 'A';
    if (score >= 60) return 'B';
    if (score >= 40) return 'C';
    return 'D';
  }

  private gradeColor(g: string): string {
    switch (g) {
      case 'S+': case 'S': return '#ffea00';
      case 'A': return '#00f0ff';
      case 'B': return '#00e676';
      case 'C': return '#ff9100';
      default: return '#ff1744';
    }
  }
}
